'use client';

import React, { TouchEvent, useState } from 'react';
import { LuChevronsLeftRight } from 'react-icons/lu';

import { BeforeAndAfterProps } from '../../_blocks/BeforeAndAfterGraphQL/types';
import classes from './BeforeAndAfter.module.scss';
import { DebugJsonBlock } from '../DebugJsonBlock';
import { Media } from '../Media';
import type { Media as MediaType } from '../../../payload/payload-types';
import { Gutter } from '../Gutter';
import { renderTrademarkText } from '../TrademarkText';

export const BeforeAndAfter: React.FC<{
  className?: string;
  beforeAndAfter: BeforeAndAfterProps;
}> = ({ className, beforeAndAfter }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = (target: HTMLDivElement, clientX: number) => {
    const rect = target.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = Math.max(0, Math.min((x / rect.width) * 100, 100));

    setSliderPosition(percent);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!isDragging) return;

    handleMove(event.currentTarget, event.clientX);
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    handleMove(event.currentTarget, event.changedTouches[0].clientX);
  };

  const handleStart = () => {
    setIsDragging(true);
  };

  const handleEnd = () => {
    setIsDragging(false);
  };

  const { beforeImage, afterImage } = beforeAndAfter;

  const beforeLabelText = beforeAndAfter.beforeLabel?.text || beforeAndAfter.beforeLabel_legacy;
  const beforeLabelTag = beforeAndAfter.beforeLabel?.tag || 'span';
  const BeforeLabelTag = beforeLabelTag as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'p' | 'div';

  const afterLabelText = beforeAndAfter.afterLabel?.text || beforeAndAfter.afterLabel_legacy;
  const afterLabelTag = beforeAndAfter.afterLabel?.tag || 'span';
  const AfterLabelTag = afterLabelTag as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'p' | 'div';
  const beforeImageResource = beforeImage as unknown as MediaType;
  const afterImageResource = afterImage as unknown as MediaType;
  return (
    <Gutter className={[className, classes.wrapper].filter(Boolean).join(' ')}>
      <div
        className={classes.sliderWrapper}
        onMouseMove={handleMouseMove}
        onMouseDown={handleStart}
        onMouseUp={handleEnd}
        onTouchMove={handleTouchMove}
        onTouchStart={handleStart}
        onTouchEnd={handleEnd}
        onTouchCancel={handleEnd}
      >
        {beforeLabelText && (
          <BeforeLabelTag className={classes.beforeImageLabel}>{renderTrademarkText(beforeLabelText)}</BeforeLabelTag>
        )}
        <div className={classes.before}>
          <Media
            resource={beforeImageResource}
            className={classes.image}
            draggable={false}
            imageSize="widescreen"
          />
        </div>
        <div className={classes.slider} style={{ left: `${sliderPosition}%` }}>
          <div className={classes.sliderHandle}>
            <LuChevronsLeftRight color="white" size="40px" />
          </div>
        </div>
        <div
          className={classes.after}
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        >
          <Media
            resource={afterImageResource}
            className={classes.image}
            draggable={false}
            imageSize="widescreen"
          />
        </div>
        {afterLabelText && (
          <AfterLabelTag className={classes.afterImageLabel}>{renderTrademarkText(afterLabelText)}</AfterLabelTag>
        )}
      </div>
      <DebugJsonBlock block={beforeAndAfter} />
    </Gutter>
  );
};

export default BeforeAndAfter;
